import { useRef, type FC, type MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import { BuyerProductTile } from 'dibs-buyer-product-tile/exports/BuyerProductTile';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem';
import { VERTICAL_OFFSET_RESP_IMG, SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import classNames from 'classnames';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useQuickView } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { useFavoritesItem } from 'dibs-portfolio-folder/exports/useFavoritesItem';

import styles from './SharedRespRelatedItem.scss';

import { type SharedRespRelatedItem_item$key } from './__generated__/SharedRespRelatedItem_item.graphql';
import { type SharedRespRelatedItem_user$key } from './__generated__/SharedRespRelatedItem_user.graphql';
import { type SharedRespRelatedItem_viewer$key } from './__generated__/SharedRespRelatedItem_viewer.graphql';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { useBuyerProductTileSize } from '../hooks/useBuyerProductTileSize';

const SMALL_TILE_TYPE = 'small';
const X_SMALL_TILE_TYPE = 'xSmall';
export type itemTileType = typeof X_SMALL_TILE_TYPE | typeof SMALL_TILE_TYPE;

type Props = {
    item: SharedRespRelatedItem_item$key;
    user: SharedRespRelatedItem_user$key | null | undefined;
    viewer: SharedRespRelatedItem_viewer$key | null | undefined;
    customClassName?: string | null;
    index: number;
    currency: string;
    onClick?: MouseEventHandler;
    favorites: FavoritesProviderChildrenProps;
    lazyLoadStartIndex?: number;
    showLightBox?: boolean;
    showMeasurements?: boolean;
    hideTileImagePadding?: boolean;
    tileType?: itemTileType;
    badgeBgColor?: string;
    showNoReserveBadge?: boolean;
    onContentVisible?: () => void;
};

export const SharedRespRelatedItem: FC<Props> = ({
    index,
    customClassName,
    item: itemRef,
    onClick,
    user: userRef,
    viewer: viewerRef,
    favorites,
    currency,
    lazyLoadStartIndex = 0,
    showLightBox = false,
    showMeasurements,
    hideTileImagePadding,
    tileType = SMALL_TILE_TYPE,
    badgeBgColor,
    showNoReserveBadge,
    onContentVisible,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SharedRespRelatedItem_viewer on Viewer {
                ...ItemTile_viewer
                ...BuyerProductTile_viewer
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment SharedRespRelatedItem_item on Item
            @argumentDefinitions(priceBookName: { type: "String" }) {
                isFurniture
                firstPhotoWebPath(size: thumb)
                ...ItemTile_item @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                ...BuyerProductTile_item
                    @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
                ...FavoritesItem_item
                ...SharedFavoriteItem_item
                ...useFavoritesItem_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment SharedRespRelatedItem_user on User {
                canShowSellerName
                ...useFavoritesItem_user
            }
        `,
        userRef
    );

    const tileElementRef = useRef(null);
    const {
        isFolderVisible,
        showFolderPlusIcon,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        onMouseEnter,
        onMouseLeave,
    } = useFavoritesItem({
        item,
        user,
        applyMouseEvents: true,
        tileElementRef,
    });
    const { openQuickView, closeQuickView } = useQuickView();
    const { canShowSellerName } = user || {};
    const { firstPhotoWebPath, isFurniture } = item || {};
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const {
        isTablet,
        isSsrOnlyBot = false,
        isContentVisibilitySbVariant,
        isItemTileRedesignTestVariant,
    } = useServerVarsContext();

    const isClient = useClientState();
    const tileSize = useBuyerProductTileSize();

    if (isItemTileRedesignTestVariant) {
        return (
            <BuyerProductTile
                viewer={viewer}
                itemSearch={null}
                item={item}
                user={null}
                afterDisplay={isClient}
                currency={currency}
                imageBackgroundColor="white"
                index={index}
                itemsPerRow={lazyLoadStartIndex}
                onClick={onClick}
                onContentVisible={onContentVisible}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                size={tileSize}
                favoritesComponent={
                    <SharedFavoriteItem
                        item={item}
                        isFolderVisible={isFolderVisible}
                        showFolderPlusIcon={showFolderPlusIcon}
                        onUnFavorited={onUnFavorited}
                        onFolderActionStart={onFolderActionStart}
                        onFolderActionEnd={onFolderActionEnd}
                        fetchFolderOnMouseOver
                        favorites={favorites}
                        tileSize={tileSize}
                    />
                }
                showPrice={isClient || isSsrOnlyBot}
                tileElementRef={tileElementRef}
            />
        );
    }
    const favoritesComponent = favorites ? (
        <div className={classNames(styles.favorites, styles[tileType])}>
            <FavoritesItem
                wrapperClass={styles.favoritesWrapper}
                heartIconWrapperClass={styles.favoritesIconWrapper}
                folderIconWrapperClass={styles.favoritesIconWrapper}
                {...favorites}
                item={item}
                itemImage={firstPhotoWebPath || ''}
                fetchFolder={favorites.fetchFolder && isFolderVisible}
                showFolderPlusIcon={showFolderPlusIcon}
                onUnFavorited={onUnFavorited}
                onFolderActionStart={onFolderActionStart}
                onFolderActionEnd={onFolderActionEnd}
            />
        </div>
    ) : null;
    // Default Layout
    return (
        <div
            className={classNames(styles.item, styles[tileType], customClassName)}
            ref={tileElementRef}
        >
            <ItemTile
                disableContentMargin={tileType === X_SMALL_TILE_TYPE}
                index={index}
                item={item}
                currency={currency}
                itemsPerRow={lazyLoadStartIndex}
                showSellerName={isClient && canShowSellerName}
                showCreatorName={canShowCreatorName}
                onClick={onClick}
                onContentVisible={onContentVisible}
                showQuickView={!!openQuickView && !!closeQuickView}
                showLightBox={showLightBox}
                alignCenter={showLightBox}
                disableBorder
                useLoFiLazyLoader
                imageSize={tileType === X_SMALL_TILE_TYPE ? 'imageXSmall' : 'imageSmall'}
                itemSearch={null}
                imageLoadVerticalOffset={VERTICAL_OFFSET_RESP_IMG}
                viewer={viewer}
                srcSetSizes={SRC_SET_SIZES_TILE}
                showMSKUSwatch={false}
                favoritesComponent={favoritesComponent}
                showMeasurements={showMeasurements}
                hideImagePadding={hideTileImagePadding}
                badgeBgColor={badgeBgColor}
                showNoReserveBadge={showNoReserveBadge}
                showPrice={isClient || isSsrOnlyBot}
                isMobile={isTablet}
                shouldAutoHideContent={isContentVisibilitySbVariant}
            />
        </div>
    );
};
